import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { SUPABASE_URL } from "@/constants/supabase";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  reason: z.string().optional(),
  confirmUnsubscribe: z.boolean().refine((val) => val === true, {
    message: "You must confirm that you want to unsubscribe.",
  }),
});

export default function Unsubscribe() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoProcessed, setAutoProcessed] = useState(false);

  const searchParams = useSearchParams()[0];
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const type = searchParams.get("type") || "all";

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email,
      reason: "",
      confirmUnsubscribe: false,
    },
  });

  // Auto-process unsubscribe if email and token are provided
  useEffect(() => {
    if (email && token && !isSubmitted && !autoProcessed) {
      handleAutoUnsubscribe();
    }
  }, [email, token, isSubmitted, autoProcessed]);

  // Function to automatically process unsubscribe from URL parameters
  async function handleAutoUnsubscribe() {
    setIsLoading(true);
    setError(null);
    setAutoProcessed(true);

    try {
      // Call the unsubscribe endpoint
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/unsubscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email, token, type }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to process unsubscribe request");
      }

      setIsSubmitted(true);
      toast({
        title: "Unsubscribed Successfully",
        description: `You have been unsubscribed from ${getUnsubscribeTypeText()}.`,
      });
    } catch (err) {
      console.error("Error processing unsubscribe:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      toast({
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "Failed to process unsubscribe request",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  // Function to handle manual form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);

    try {
      // Generate a token if one wasn't provided in the URL
      let tokenToUse = token;

      if (!tokenToUse) {
        // Generate a new token
        const tokenResponse = await fetch(
          `${SUPABASE_URL}/functions/v1/email-preferences/generate-token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${supabase.auth.getSession()}`,
            },
            body: JSON.stringify({
              email: values.email,
              purpose: "unsubscribe",
            }),
          }
        );

        const tokenData = await tokenResponse.json();

        if (!tokenResponse.ok) {
          throw new Error(tokenData.error || "Failed to generate token");
        }

        tokenToUse = tokenData.token;
      }

      // Call the unsubscribe endpoint
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/unsubscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({
            email: values.email,
            token: tokenToUse,
            type,
            reason: values.reason,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to process unsubscribe request");
      }

      setIsSubmitted(true);
      toast({
        title: "Unsubscribed Successfully",
        description: `You have been unsubscribed from ${getUnsubscribeTypeText()}.`,
      });
    } catch (err) {
      console.error("Error processing unsubscribe:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      toast({
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "Failed to process unsubscribe request",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const getUnsubscribeTypeText = () => {
    switch (type) {
      case "marketing":
        return "marketing communications";
      case "document_requests":
        return "document request notifications";
      case "information_requests":
        return "information request notifications";
      case "service_updates":
        return "service update notifications";
      default:
        return "all communications";
    }
  };

  return (
    <EmailPageLayout
      title="Unsubscribe from Emails"
      description={`Manage your subscription to ${getUnsubscribeTypeText()}`}
    >
      {isLoading && !error && !isSubmitted ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-center text-muted-foreground">
              Processing your unsubscribe request...
            </p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Error
            </CardTitle>
            <CardDescription>
              We encountered a problem processing your unsubscribe request.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground mb-4">
              This may be because your link has expired or is invalid. You can
              try entering your email address below to unsubscribe.
            </p>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="your.email@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    "Try Again"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      ) : isSubmitted ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Unsubscribed Successfully
            </CardTitle>
            <CardDescription>
              You have been unsubscribed from {getUnsubscribeTypeText()}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              You will no longer receive these emails from Numberz Insight. If
              you change your mind, you can update your preferences at any time.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/email/preferences")}
              >
                Manage All Preferences
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  window.location.href = `/email/preferences?email=${encodeURIComponent(
                    email
                  )}`;
                }}
              >
                Update Preferences
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Alert variant="destructive" className="text-white dark:text-white">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="text-white dark:text-white">
                You are about to unsubscribe
              </AlertTitle>
              <AlertDescription className="text-white dark:text-white">
                You will no longer receive {getUnsubscribeTypeText()} from
                Numberz Insight.
              </AlertDescription>
            </Alert>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white dark:text-white">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="your.email@example.com"
                      {...field}
                      className="text-black dark:text-white"
                    />
                  </FormControl>
                  <FormDescription className="text-gray-200 dark:text-gray-300">
                    Confirm the email address you want to unsubscribe.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white dark:text-white">
                    Reason for Unsubscribing (Optional)
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please let us know why you're unsubscribing so we can improve our communications."
                      {...field}
                      className="text-black dark:text-white"
                    />
                  </FormControl>
                  <FormDescription className="text-gray-200 dark:text-gray-300">
                    Your feedback helps us improve our service.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmUnsubscribe"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-white dark:text-white">
                      Confirm Unsubscribe
                    </FormLabel>
                    <FormDescription className="text-gray-200 dark:text-gray-300">
                      I understand that I will no longer receive{" "}
                      {getUnsubscribeTypeText()} from Numberz Insight.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => (window.location.href = "/email/preferences")}
              >
                Manage All Preferences Instead
              </Button>
              <Button type="submit" variant="destructive" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Unsubscribe"
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </EmailPageLayout>
  );
}
